import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js'

import { type AppSupabaseClient } from '@/platform/supabase/client'

import {
  type Availability,
  availabilitySchema,
  type UnavailableReason,
  type Extraction,
  ExtractionError,
  type ExtractRequest,
  extractResponseSchema,
  type MaterialExtractionService,
} from './extraction'

/**
 * `MaterialExtractionService` over the `extract-material` Edge Function. The
 * caller's session JWT travels with every call (supabase-js adds it), so the
 * function checks the learner against the caller's family through RLS. No
 * provider key or service-role credential is ever in the browser (SEC-A).
 */

export const EXTRACT_FUNCTION = 'extract-material'

/** Carries the learner session token to the function (D-16). Never logged. */
export const SESSION_HEADER = 'x-learner-session'

/** Client-side ceiling; the function's own provider timeout is shorter. */
export const EXTRACT_TIMEOUT_MS = 90_000

async function errorBody(error: unknown): Promise<unknown> {
  if (!(error instanceof FunctionsHttpError)) return null
  const context: unknown = error.context
  if (!(context instanceof Response)) return null
  try {
    return (await context.json()) as unknown
  } catch {
    return null
  }
}

/**
 * Why a failed availability request failed, from the HTTP status and the
 * function's own `{ ok: false, error }` body when there is one (the gateway's
 * 401 has a different body, so the status decides). Only the status and a
 * known code are returned or logged: never a token, header or body text.
 */
async function classifyAvailabilityError(
  error: unknown,
): Promise<{ why: UnavailableReason; status: number | null; code: string | null }> {
  if (error instanceof FunctionsFetchError)
    return { why: 'unreachable', status: null, code: null }
  if (error instanceof FunctionsRelayError)
    return { why: 'provider_failed', status: null, code: 'relay' }
  if (!(error instanceof FunctionsHttpError))
    return { why: 'unreachable', status: null, code: null }

  const context: unknown = error.context
  const status = context instanceof Response ? context.status : null
  const body = await errorBody(error)
  const parsed = extractResponseSchema.safeParse(body)
  const code = parsed.success && !parsed.data.ok ? parsed.data.error : null

  if (code === 'not_authenticated') return { why: 'not_authenticated', status, code }
  if (code === 'provider_failed' || code === 'provider_timeout')
    return { why: 'provider_failed', status, code }
  if (status === 401) return { why: 'not_authenticated', status, code }
  if (status !== null && status >= 500) return { why: 'provider_failed', status, code }
  return { why: 'invalid_response', status, code }
}

/** Status and error code only (FR-169): safe to read in the browser console. */
function logAvailability(
  why: UnavailableReason,
  status: number | null,
  code: string | null,
): void {
  console.warn('extract-material availability', { why, status, code })
}

const isSubject = (x: string): x is 'mathematics' | 'english' =>
  x === 'mathematics' || x === 'english'

export function createSupabaseExtractionService(
  client: AppSupabaseClient,
): MaterialExtractionService {
  return {
    async availability(sessionToken: string): Promise<Availability> {
      try {
        const res = await client.functions.invoke<unknown>(EXTRACT_FUNCTION, {
          method: 'GET',
          headers: { [SESSION_HEADER]: sessionToken },
        })
        const error: unknown = res.error
        const data: unknown = res.data
        if (error !== null && error !== undefined) {
          const c = await classifyAvailabilityError(error)
          logAvailability(c.why, c.status, c.code)
          return { available: false, why: c.why }
        }
        const parsed = availabilitySchema.safeParse(data)
        if (!parsed.success) {
          logAvailability('invalid_response', 200, null)
          return { available: false, why: 'invalid_response' }
        }
        const a = parsed.data
        const u = a.upload
        if (!u.entitled) return { available: false, why: 'not_entitled' }
        if (!u.learnerSession) return { available: false, why: 'learner_session_invalid' }
        if (u.restricted)
          return {
            available: false,
            why: 'restricted',
            restrictedUntil: u.restrictedUntil,
          }
        if (!u.consent) return { available: false, why: 'consent_required' }
        return a.available && a.subjects.includes('mathematics')
          ? {
              available: true,
              provider: a.provider,
              notice: a.notice,
              maxBytes: a.maxBytes,
              subjects: a.subjects.filter(isSubject),
              ...(a.syntheticFixtureSubjects === undefined
                ? {}
                : {
                    syntheticFixtureSubjects:
                      a.syntheticFixtureSubjects.filter(isSubject),
                  }),
            }
          : { available: false, why: 'not_configured' }
      } catch {
        logAvailability('unreachable', null, null)
        return { available: false, why: 'unreachable' }
      }
    },

    async extract(sessionToken: string, request: ExtractRequest): Promise<Extraction> {
      // No learner id is sent: the server takes the learner from the session.
      const form = new FormData()
      form.set('file', request.file)
      form.set('subject', request.subject)
      form.set('request_id', request.requestId)
      if (request.syntheticFixture === true) form.set('synthetic_fixture', 'true')

      let data: unknown
      let error: unknown
      try {
        const res = await client.functions.invoke<unknown>(EXTRACT_FUNCTION, {
          method: 'POST',
          body: form,
          headers: { [SESSION_HEADER]: sessionToken },
          signal: request.signal,
          timeout: EXTRACT_TIMEOUT_MS,
        })
        data = res.data
        error = res.error
      } catch {
        throw new ExtractionError(request.signal.aborted ? 'cancelled' : 'network')
      }
      if (request.signal.aborted) throw new ExtractionError('cancelled')

      const body = error === null || error === undefined ? data : await errorBody(error)
      const parsed = extractResponseSchema.safeParse(body)
      if (!parsed.success) {
        // No usable body: the request did not get through, or the answer is not ours.
        throw new ExtractionError(
          error !== null && error !== undefined && body === null
            ? 'network'
            : 'invalid_response',
        )
      }
      if (!parsed.data.ok)
        throw new ExtractionError(parsed.data.error, parsed.data.restrictedUntil ?? null)
      return parsed.data.extraction
    },
  }
}
